import { memo, useCallback, useEffect, useState } from 'react'
import { styled, Typography } from '@mui/material'
import MuiIconButton from '@mui/material/IconButton'
import { getFileUrl } from '@weblite/file'
import { Workspace } from '@weblite/ts-action/workspace'
import { ProfileIcon, WAppLogoType, WSearchIcon } from '@weblite/icons'
import {
  useCurrentWorkSpaceId,
  useDisplayedConnectionStatus,
} from '$comp/main/react-hook'
import { openRoomListSearch, switchToMyAccount } from '$logic/router'
import {
  APP_BAR_HEIGHT_CSS_VAR,
  CHAT_LIST_APP_BAR_HEIGHT,
} from '$common/app-bar/app-bar.constants'
import { MuiCircularLoading } from '$common/loading/mui-circular-loading'
import {
  IS_STORY_ENABLED,
  SHOULD_CATEGORIZE_CHATS,
} from '$func/environmentInfo'
import {
  StoryRibbonsApi,
  useIsStoryRibbonOpen,
} from '$comp/_app/components/story/story-ribbons-api'
import { useServerStoryRibbonsWithSeparatedClientUserStory } from '$logic/story/react-hooks'
import { toLocale } from '$func/text'
import { StoryToggler } from '$comp/_app/components/story/story-toggler'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { t } from '$i18n'
import { useCurrentWorkspace, DEFAULT_WORKSPACE_ID } from '$logic/workspace'
import { AvatarBase } from '$common/avatar'
import { DEFAULT_TRANSITION_MS } from '$comp/_app/components/story/constants'
import { createCallbackManager } from '$func/callbacks'
import { WORKSPACE_ENABLED } from '$config/env'
import { chatAppbarIsExpandedDefaultValue } from './chat-app-bar-api'

export const chatAppbarCallbackManager = createCallbackManager()

export const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  backgroundColor: theme.palette.neutral000,
  flexDirection: 'column',
  padding: '8px 4px',
  boxSizing: 'border-box',
  height: APP_BAR_HEIGHT_CSS_VAR,
  gap: '4px',
  transition: `height ${DEFAULT_TRANSITION_MS}ms`,
}))
export const Container = styled('div')({
  display: 'flex',
  height: CHAT_LIST_APP_BAR_HEIGHT,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'start',
  boxSizing: 'border-box',
  zIndex: 2,
})

const ConnectionStatusWrapper = styled('div')`
  display: flex;
  gap: 8px;
  margin-right: 8px;
  color: ${({ theme }) => theme.palette.secondary700};
`

const IconButton = styled(MuiIconButton)`
  color: ${({ theme }) => theme.palette.neutral000};
`

export const ChatAppBar = memo(function ChatAppBarComponent() {
  const connectionStatus = useDisplayedConnectionStatus()

  const { open: isStoryRibbonOpen } = useIsStoryRibbonOpen()

  const { nonClientRibbonStories: storyRibbons } =
    useServerStoryRibbonsWithSeparatedClientUserStory()

  const storyCount = storyRibbons.length

  const onSearchIcon = () => {
    openRoomListSearch()
    analite('CHATLIST_SEARCH_CLICK')
  }

  const currentWorkspace = useCurrentWorkspace()
  const currentWorkspaceId = useCurrentWorkSpaceId() // NOTE: useCurrentWorkSpaceId() is not same with useCurrentWorkspace().id
  // useCurrentWorkSpace can return undefined but useCurrentWorkSpaceId() will return DEFAULT_WORKSPACE_ID if no workspace is selected
  const workspaceIsSelected =
    currentWorkspace && currentWorkspace.id !== DEFAULT_WORKSPACE_ID

  const isCurrentWorkSpaceDefault =
    currentWorkspaceId === DEFAULT_WORKSPACE_ID || !WORKSPACE_ENABLED

  const [isExpanded, setIsExpanded] = useState(chatAppbarIsExpandedDefaultValue)

  const setIsExpandedCallback = useCallback((newExpandedValue: boolean) => {
    setIsExpanded(newExpandedValue)
  }, [])

  useEffect(() => {
    chatAppbarCallbackManager.addCallback(setIsExpandedCallback)
    return () => chatAppbarCallbackManager.removeCallback(setIsExpandedCallback)
  }, [])

  return (
    <Root>
      <Container>
        <IconButton onClick={onSearchIcon}>
          <WSearchIcon sx={{ color: 'neutral900' }} />
        </IconButton>

        {workspaceIsSelected && isExpanded ? null : connectionStatus ? (
          <ConnectionStatusBox
            connectionStatus={connectionStatus}
            color="neutral900"
          />
        ) : workspaceIsSelected ? null : !isStoryRibbonOpen &&
          IS_STORY_ENABLED &&
          isCurrentWorkSpaceDefault ? (
          <StoryTogglerContainer>
            {!connectionStatus && (
              <Typography
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  StoryRibbonsApi.toggleStoryRibbons({ isShown: true })
                }
                color="neutral1000"
                variant="subtitle2"
                noWrap
              >
                {storyCount > 0
                  ? t('story.nStories')(toLocale(storyCount))
                  : t('story.stories')}
              </Typography>
            )}

            <StoryToggler
              sx={{ flexShrink: 0, height: '100%' }}
              // eslint-disable-next-line no-underscore-dangle
              size={AvatarSize._2XS}
              onClick={() => {
                StoryRibbonsApi.toggleStoryRibbons({ isShown: true })
              }}
            />
          </StoryTogglerContainer>
        ) : null
        // <WAppLogoType
        //   sx={{ alignSelf: 'center', width: '64px', mr: '8px' }}
        // />
        }

        <IconButton onClick={switchToMyAccount}>
          <ProfileIcon sx={{ color: 'neutral900' }} />
        </IconButton>
      </Container>

      <div
        style={{
          height:
            workspaceIsSelected && isExpanded
              ? `calc(${APP_BAR_HEIGHT_CSS_VAR} - ${CHAT_LIST_APP_BAR_HEIGHT}px)`
              : 0,
          overflow: workspaceIsSelected && isExpanded ? 'auto' : 'hidden',
          borderRadius: '12px',
          position: 'relative',
          padding: workspaceIsSelected && isExpanded ? '4px 8px' : 0,
          backgroundColor: currentWorkspace?.color,
          display: 'flex',
          transition: `height ${DEFAULT_TRANSITION_MS}ms`,
        }}
      >
        {connectionStatus ? (
          <ConnectionStatusBox
            connectionStatus={connectionStatus}
            color="neutral000"
          />
        ) : null}
      </div>

      {workspaceIsSelected && !connectionStatus && (
        <WorkspaceName expanded={isExpanded} />
      )}
    </Root>
  )
})

export const ChatAppBarRoot = styled('div')<{ backgroundColor?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  padding: 8px;
  box-sizing: border-box;
  height: ${CHAT_LIST_APP_BAR_HEIGHT}px;
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor || theme.palette.neutral000};
  color: ${({ theme, backgroundColor }) =>
    backgroundColor ? theme.palette.neutral000 : theme.palette.neutral900};
  border-bottom: ${({ theme }) =>
    SHOULD_CATEGORIZE_CHATS ? 'none' : `1px solid ${theme.palette.neutral200}`};
  transition: height ${DEFAULT_TRANSITION_MS}ms;
`

const StoryTogglerContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-grow: 1;
  height: 100%;
  overflow: hidden;
`

const WorkspaceNameContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'center',
  transform: `translateX(-50%)`,
  transition: 'top 250ms',

  position: 'absolute',
  top: '4px',
  left: '50%',
  width: '100%',
  '&.expanded': {
    top: '38px',
  },
})

function WorkspaceName({ expanded }: { expanded: boolean }) {
  const { name, icon } = useCurrentWorkspace() as Workspace

  return (
    <WorkspaceNameContainer className={expanded ? 'expanded' : ''}>
      <AvatarBase
        src={getFileUrl(icon)}
        squareMode
        sx={{
          border: '1px solid',
          borderColor: `neutral300`,
          transition: `all ${DEFAULT_TRANSITION_MS}ms`,
        }}
        size={expanded ? AvatarSize.LG : AvatarSize.SM}
      />
      <Typography
        variant={expanded ? 'h3' : 'h2'}
        color={expanded ? 'neutral000' : 'neutral900'}
      >
        {name}
      </Typography>
    </WorkspaceNameContainer>
  )
}

export function ConnectionStatusBox({
  connectionStatus,
  color = 'neutral900',
}: {
  connectionStatus: string
  color?: string
}) {
  return (
    <ConnectionStatusWrapper style={{ margin: 'auto' }}>
      <MuiCircularLoading color={color} size={24} />
      <Typography color={color} variant="subtitle1">
        {connectionStatus}
      </Typography>
    </ConnectionStatusWrapper>
  )
}
